/**
 * @voltxampere/engine — Worker protocol types + Zod validation (Sprint 9)
 *
 * Blueprint §9's worker surface is our contract between the main
 * thread (UI) and the engine thread. Every message that crosses
 * the `postMessage` boundary is one of:
 *
 *   - `EngineRequest` — main thread → worker. Discriminated union
 *     of 8 kinds: `init | updateCircuit | runAnalysis |
 *     runAssertions | startStream | stopStream | cancel | destroy`.
 *   - `EngineResponse` — worker → main thread. Discriminated on 8
 *     kinds too: `ready | analysisResult | assertionResult |
 *     frame | progress | error | cancelled | destroyed`.
 *
 * We express each union with Zod so incoming payloads can be
 * strictly validated at the boundary (Hard Rule §3.3). Invalid
 * inputs do not reach the handler — they short-circuit into a
 * typed `error` response so the main thread always learns about
 * protocol violations.
 *
 * Sprint 9 deviation from SPECS §4: the SPECS shape keys the
 * union on `kind` (values `dc/transient/ac/cancel/ping`), matching
 * the SPICE-y way of thinking. The Sprint 9 prompt explicitly
 * asks for Blueprint §9's shape (keys on `type` + streaming +
 * runAssertions), which is richer. We follow the prompt and log
 * this as D1 in SPRINT_9_REPORT. Sprint 10+ will reconcile via an
 * ADR, likely by collapsing both onto §9's `type` keyed variant
 * and aliasing the SPECS labels inside a sub-discriminator.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { CircuitSchema } from "@voltxampere/schema";
import { z } from "zod";
/** UUID-shaped correlation id tying a response back to its request. */
export const RequestIdSchema = z.string().uuid();
/** The three analysis regimes the engine exposes through the worker. */
export const AnalysisKindSchema = z.enum(["DC", "TRANSIENT", "AC"]);
/** Sprint 20: per-run override for the AC sweep configuration. Each
 * field is optional and falls back to `circuit.simulationSettings.ac`
 * when absent — additive shape so existing DC/transient requests keep
 * working unchanged. */
export const ACSweepOverrideSchema = z
    .object({
    fStart: z.number().positive().optional(),
    fStop: z.number().positive().optional(),
    points: z.number().int().min(2).optional(),
    sweep: z.enum(["dec", "oct", "lin"]).optional(),
})
    .strict();
/** Parameters for a `runAnalysis` request, thin per-analysis
 * overrides of the circuit's own `simulationSettings`. */
export const AnalysisRequestSchema = z
    .object({
    kind: AnalysisKindSchema,
    /** Per-run override for `tstop` (transient). Defaults to the circuit's setting. */
    tstop: z.number().positive().optional(),
    /** Per-run override for `maxIterations` (DC). */
    maxIterations: z.number().int().positive().optional(),
    /** Stop after receiving this many transient samples. Sprint 10+ use. */
    sampleCap: z.number().int().positive().optional(),
    /** Sprint 20 (D-20-4): per-run AC sweep override. Each field is
     * optional; absent fields fall back to the circuit's
     * `simulationSettings.ac`. */
    acSweep: ACSweepOverrideSchema.optional(),
})
    .strict();
/** Optional engine-wide init settings. */
export const EngineOptionsSchema = z
    .object({
    /** Warmup the stamp registry at init time; defaults true. */
    registerStamps: z.boolean().default(true),
})
    .strict();
// -------------------------------------------------------------
// EngineRequest
// -------------------------------------------------------------
export const EngineRequestSchema = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("init"),
        id: RequestIdSchema,
        circuit: CircuitSchema,
        options: EngineOptionsSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal("updateCircuit"),
        id: RequestIdSchema,
        circuit: CircuitSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("runAnalysis"),
        id: RequestIdSchema,
        analysis: AnalysisRequestSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("runAssertions"),
        id: RequestIdSchema,
        /** Which analysis kind's attached assertions should be evaluated. */
        analysis: AnalysisKindSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("startStream"),
        id: RequestIdSchema,
        /** Upper bound on frame rate; the worker may emit fewer if the
         * solver takes longer than `1/fps` per solve. */
        fps: z.number().int().min(1).max(120).default(60),
    })
        .strict(),
    z
        .object({
        type: z.literal("stopStream"),
        id: RequestIdSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("cancel"),
        id: RequestIdSchema,
        /** Request id of the in-flight analysis to abort. */
        targetId: RequestIdSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("destroy"),
        id: RequestIdSchema,
    })
        .strict(),
]);
// -------------------------------------------------------------
// EngineResponse
// -------------------------------------------------------------
export const FrameStateSchema = z
    .object({
    timestamp: z.number(),
    nodeVoltages: z.record(z.string(), z.number()),
    branchCurrents: z.record(z.string(), z.number()),
    converged: z.boolean(),
    iterations: z.number().int().nonnegative(),
    warnings: z.array(z.string()).default([]),
})
    .strict();
export const AssertionResultWireSchema = z
    .object({
    assertionId: z.string(),
    sourceText: z.string(),
    passed: z.boolean(),
    skipped: z.boolean(),
    message: z.string(),
    actual: z.union([z.string(), z.number()]),
    expected: z.union([z.string(), z.number()]),
    margin: z.number(),
    marginUnit: z.string(),
    analysisKind: AnalysisKindSchema,
    resolutionError: z.string().optional(),
})
    .strict();
/**
 * Analysis result envelope. The engine's internal `DCResult` etc.
 * have Map fields that don't survive structured-clone well, so we
 * flatten them to plain records before posting.
 *
 * Sprint 10.0 (B-P1-03 closure): `assertionResults` is now carried
 * directly with the analysis envelope as an OPTIONAL field. The
 * solver evaluates assertions inline (`runAssertionsOnDC` /
 * `runAssertionsOnTransient`) so the work is already done; sending
 * the results with the same response avoids the redundant
 * `runAssertions` round-trip Sprint 9 forced. Backward-compatible
 * — older clients ignore the field.
 */
export const AnalysisResultSchema = z
    .object({
    kind: AnalysisKindSchema,
    converged: z.boolean(),
    iterations: z.number().int().nonnegative(),
    nodeVoltages: z.record(z.string(), z.number()),
    branchCurrents: z.record(z.string(), z.number()),
    warnings: z.array(z.string()),
    /** Generic key-value bag for kind-specific extras (tier,
     * residual, clamp events, transient time series length etc). */
    meta: z.record(z.string(), z.unknown()).default({}),
    /** Sprint 10.0: optional inline assertion outcomes evaluated by
     * the solver during the same run. Empty array when the circuit
     * had no assertions; absent when the solver pre-dates the
     * Sprint 10.0 wire extension. */
    assertionResults: z.array(AssertionResultWireSchema).optional(),
})
    .strict();
export const EngineErrorSchema = z
    .object({
    code: z.enum([
        "INVALID_REQUEST",
        "NOT_INITIALISED",
        "STREAM_ALREADY_RUNNING",
        "STREAM_NOT_RUNNING",
        "CANCELLED",
        "CONVERGENCE_FAILED",
        "TOPOLOGY_ERROR",
        "STREAM_SOLVE_FAIL",
        "UNKNOWN",
    ]),
    message: z.string(),
    stack: z.string().optional(),
    targetId: RequestIdSchema.optional(),
})
    .strict();
export const EngineResponseSchema = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("ready"),
        id: RequestIdSchema,
        engineVersion: z.string(),
    })
        .strict(),
    z
        .object({
        type: z.literal("frame"),
        id: RequestIdSchema,
        timestamp: z.number(),
        state: FrameStateSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("analysisResult"),
        id: RequestIdSchema,
        result: AnalysisResultSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("assertionResult"),
        id: RequestIdSchema,
        results: z.array(AssertionResultWireSchema),
    })
        .strict(),
    z
        .object({
        type: z.literal("progress"),
        id: RequestIdSchema,
        analysis: AnalysisKindSchema,
        percent: z.number().min(0).max(100),
        stage: z.string(),
    })
        .strict(),
    z
        .object({
        type: z.literal("error"),
        id: RequestIdSchema,
        error: EngineErrorSchema,
    })
        .strict(),
    z
        .object({
        type: z.literal("cancelled"),
        id: RequestIdSchema,
        targetId: RequestIdSchema,
    })
        .strict(),
    // Sprint 10.0 (B-P1-01 closure): dedicated `streamStopped` reply
    // for the `stopStream` request. Pre-Sprint-10 the handler reused
    // `destroyed` for both stopStream and destroy, conflating two
    // semantically distinct lifecycle events on the wire. ADR-0010's
    // four-touch invariant applies — Zod schema + handler + client +
    // test all change together.
    z
        .object({
        type: z.literal("streamStopped"),
        id: RequestIdSchema,
        /** Wall-clock seconds (matches `frame.timestamp` units). */
        ts: z.number(),
    })
        .strict(),
    z
        .object({
        type: z.literal("destroyed"),
        id: RequestIdSchema,
    })
        .strict(),
]);
//# sourceMappingURL=protocol.js.map